import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { csvFileToArray, readCSVFile } from '../../../utils/csv';

interface Data {
  headers: string[];
  data: Record<string, any>[];
}
interface CSVParserContextInterface {
  data: any;
  transformedData?: any;
  parseCSV: (fileRecord: Record<string, File[]>) => Promise<void>;
  getData: (key: string) => Data;
  setTransformedData: React.Dispatch<any>;
}

const defaultContextValue: CSVParserContextInterface = {
  data: {},
  parseCSV: () => {
    throw new Error('Not Implemented');
  },
  setTransformedData: () => {
    throw new Error('Not Implemented');
  },
  getData: () => {
    throw new Error('Not Implemented');
  },
};

const CSVParserContext = createContext<CSVParserContextInterface>(defaultContextValue);

const useCSVParserProvider = (): CSVParserContextInterface => {
  const [data, setData] = useState<any>({});
  const [transformedData, setTransformedData] = useState<any>();
  const parseCSV = useCallback(async (fileRecord: Record<string, File[]>) => {
    const fileRecordKeys = Object.keys(fileRecord);
    const fileParserPromise = fileRecordKeys
      .map((key) => {
        const files = fileRecord[key];
        if (files?.length) {
          return readCSVFile(files[0]);
        }
        return undefined;
      })
      .filter(Boolean);
    const fileContents = await Promise.all(fileParserPromise);

    let parsedContents = {};

    fileRecordKeys.forEach((key, index) => {
      const fileContentForEachKey = fileContents[index] as string;
      const parsedContent = csvFileToArray(fileContentForEachKey || '');
      parsedContents = { ...parsedContents, [key]: parsedContent || [] };
    });
    setData(parsedContents);
  }, []);

  const getData = useCallback(
    (key: string) => {
      if (data) {
        const headers = Object.keys(Object.assign({}, ...(data[key] || [])));
        return { headers, data: data[key] || [] };
      }
      return { headers: [], data: [] };
    },
    [data]
  );

  useEffect(
    () => () => {
      setData({});
    },
    []
  );

  return {
    data,
    transformedData,
    setTransformedData,
    parseCSV,
    getData,
  };
};

interface CSVParserProviderProps {
  children?: React.ReactNode;
}

export const CSVParserProvider = (props: CSVParserProviderProps) => {
  const { children } = props;
  const value = useCSVParserProvider();
  return <CSVParserContext.Provider value={value}>{children}</CSVParserContext.Provider>;
};

export const useCSVParser = () => useContext(CSVParserContext);
